import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';

import { AnalisisDatosGeneralesForm } from '@/features/analisis/common/components/AnalisisDatosGeneralesForm';
import type { AnalisisDraft } from '@/features/analisis/common/hooks/useAnalisisDraft';
import { useAnalisisDraft } from '@/features/analisis/common/hooks/useAnalisisDraft';
import { useNotify } from '@/hooks/useNotify';
import { getErrorMessage, isApiError } from '@/lib/apiError';
import { TipoAnalisis } from '@/types/enums';

import { formatNumeroAnalisis } from '@/features/analisis/utils/formatNumeroAnalisis';
import { useCoprocultivo, useCreateCoprocultivo, useUpdateCoprocultivo } from '../hooks/useCoprocultivo';
import { coprocultivoFormSchema } from '../schemas';
import type { CoprocultivoFormValues } from '../schemas';
import { ResultadoParasitologicoSection } from './ResultadoParasitologicoSection';

type TabId = 'datos' | 'resultado';

const DATOS_FIELDS: (keyof CoprocultivoFormValues)[] = [
  'fecha_analisis',
  'identificacion_muestras',
  'alimentacion',
  'ultimo_antiparasitario',
  'fecha_ultimo_tratamiento',
  'comentarios_lote',
];

const RESULTADO_FIELDS: (keyof CoprocultivoFormValues)[] = [
  'porcentaje_ostertagia',
  'porcentaje_trichostrongylus',
  'porcentaje_cooperia',
  'porcentaje_haemonchus',
  'porcentaje_oesophagostomum',
  'porcentaje_nematodirus',
  'observacion',
];

const DEFAULT_VALUES: CoprocultivoFormValues = {
  fecha_analisis: new Date(),
  identificacion_muestras: null,
  alimentacion: null,
  ultimo_antiparasitario: null,
  fecha_ultimo_tratamiento: null,
  comentarios_lote: null,
  porcentaje_ostertagia: null,
  porcentaje_trichostrongylus: null,
  porcentaje_cooperia: null,
  porcentaje_haemonchus: null,
  porcentaje_oesophagostomum: null,
  porcentaje_nematodirus: null,
  observacion: null,
};

type CoprocultivoFormDraft = Omit<CoprocultivoFormValues, 'fecha_analisis' | 'fecha_ultimo_tratamiento'> & {
  fecha_analisis: string;
  fecha_ultimo_tratamiento: string | null;
};

interface CoprocultivoFormDialogProps {
  open: boolean;
  onClose: () => void;
  uidProtocolo: string;
  uidAnalisis?: string;
  numeroProtocolo?: number;
  establecimientoNombre?: string;
  onSuccess?: () => void;
}

export function CoprocultivoFormDialog({
  open,
  onClose,
  uidProtocolo,
  uidAnalisis,
  numeroProtocolo,
  establecimientoNombre,
  onSuccess,
}: CoprocultivoFormDialogProps): JSX.Element {
  const isEdit = !!uidAnalisis;
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const notify = useNotify();

  const coproQuery = useCoprocultivo(uidProtocolo, uidAnalisis);
  const createMutation = useCreateCoprocultivo();
  const updateMutation = useUpdateCoprocultivo();

  const [tabActiva, setTabActiva] = useState<TabId>('datos');
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showLockedDialog, setShowLockedDialog] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [sumError, setSumError] = useState(false);
  const [hpgRefNumeros, setHpgRefNumeros] = useState<number[]>([]);
  const [pendingDraft, setPendingDraft] = useState<AnalisisDraft<CoprocultivoFormDraft> | null>(null);

  const { saveDraft, loadDraft, clearDraft } = useAnalisisDraft<CoprocultivoFormDraft>('coprocultivo');

  const methods = useForm<CoprocultivoFormValues>({
    resolver: zodResolver(coprocultivoFormSchema),
    defaultValues: { ...DEFAULT_VALUES, fecha_analisis: new Date() },
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, errors, submitCount },
  } = methods;

  const watchedAll = useWatch({ control });

  // Reset on open in create mode
  useEffect(() => {
    if (open && !isEdit) {
      const d = loadDraft(uidProtocolo);
      setPendingDraft(d);
      reset({ ...DEFAULT_VALUES, fecha_analisis: new Date() });
      setTabActiva('datos');
      setSumError(false);
      setSubmitError(null);
      setHpgRefNumeros([]);
    }
  }, [open, isEdit, uidProtocolo, reset, loadDraft]);

  // Auto-save draft in create mode
  useEffect(() => {
    if (!open || isEdit) return;
    saveDraft(uidProtocolo, {
      ...watchedAll,
      fecha_analisis: watchedAll.fecha_analisis?.toISOString() ?? '',
      fecha_ultimo_tratamiento: watchedAll.fecha_ultimo_tratamiento?.toISOString() ?? null,
    });
  }, [watchedAll, open, isEdit, saveDraft, uidProtocolo]);

  // Populate form in edit mode
  useEffect(() => {
    if (isEdit && coproQuery.data) {
      const c = coproQuery.data;
      const muestra = c.muestras_coprocultivo[0];
      setHpgRefNumeros(muestra?.numeros_muestras_hpg_referencia ?? []);
      reset({
        fecha_analisis: parseISO(c.fecha_analisis),
        identificacion_muestras: c.identificacion_muestras,
        alimentacion: c.alimentacion,
        ultimo_antiparasitario: c.ultimo_antiparasitario,
        fecha_ultimo_tratamiento: c.fecha_ultimo_tratamiento
          ? parseISO(c.fecha_ultimo_tratamiento)
          : null,
        comentarios_lote: c.comentarios_lote,
        porcentaje_ostertagia: muestra?.porcentaje_ostertagia ?? null,
        porcentaje_trichostrongylus: muestra?.porcentaje_trichostrongylus ?? null,
        porcentaje_cooperia: muestra?.porcentaje_cooperia ?? null,
        porcentaje_haemonchus: muestra?.porcentaje_haemonchus ?? null,
        porcentaje_oesophagostomum: muestra?.porcentaje_oesophagostomum ?? null,
        porcentaje_nematodirus: muestra?.porcentaje_nematodirus ?? null,
        observacion: muestra?.observacion ?? null,
      });
      setTabActiva('datos');
      setSumError(false);
      setSubmitError(null);
    }
  }, [isEdit, coproQuery.data, reset]);

  // Warn before tab close when there are unsaved changes
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const hasAttemptedSubmit = submitCount > 0;
  const tabDatosHasError = hasAttemptedSubmit && DATOS_FIELDS.some((f) => !!errors[f]);
  const tabResultadoHasError =
    hasAttemptedSubmit && (RESULTADO_FIELDS.some((f) => !!errors[f]) || sumError);

  const isPending = createMutation.isPending || updateMutation.isPending;

  function handleClose(): void {
    if (isDirty) {
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  }

  function handleConfirmDiscard(): void {
    setShowDiscardDialog(false);
    onClose();
  }

  function handleRestoreDraft(): void {
    if (!pendingDraft) return;
    const d = pendingDraft.data;
    reset({
      ...d,
      fecha_analisis: parseISO(d.fecha_analisis),
      fecha_ultimo_tratamiento: d.fecha_ultimo_tratamiento
        ? parseISO(d.fecha_ultimo_tratamiento)
        : null,
    });
    setTabActiva('datos');
    setPendingDraft(null);
  }

  function handleDiscardDraft(): void {
    clearDraft(uidProtocolo);
    setPendingDraft(null);
  }

  function handleSaveError(errs: FieldErrors<CoprocultivoFormValues>): void {
    const hasDatosErrors = DATOS_FIELDS.some((f) => !!errs[f]);
    const hasResultadoErrors = RESULTADO_FIELDS.some((f) => !!errs[f]);
    if (hasDatosErrors) {
      setTabActiva('datos');
    } else if (hasResultadoErrors) {
      setTabActiva('resultado');
    }
  }

  async function handleSave(values: CoprocultivoFormValues): Promise<void> {
    setSubmitError(null);

    const suma =
      (values.porcentaje_ostertagia ?? 0) +
      (values.porcentaje_trichostrongylus ?? 0) +
      (values.porcentaje_cooperia ?? 0) +
      (values.porcentaje_haemonchus ?? 0) +
      (values.porcentaje_oesophagostomum ?? 0) +
      (values.porcentaje_nematodirus ?? 0);

    if (suma !== 100 && suma !== 0) {
      setSumError(true);
      setTabActiva('resultado');
      return;
    }
    setSumError(false);

    const muestra = {
      numeros_muestras_hpg_referencia: hpgRefNumeros,
      porcentaje_ostertagia: values.porcentaje_ostertagia ?? 0,
      porcentaje_trichostrongylus: values.porcentaje_trichostrongylus ?? 0,
      porcentaje_cooperia: values.porcentaje_cooperia ?? 0,
      porcentaje_haemonchus: values.porcentaje_haemonchus ?? 0,
      porcentaje_oesophagostomum: values.porcentaje_oesophagostomum ?? 0,
      porcentaje_nematodirus: values.porcentaje_nematodirus ?? 0,
      observacion: values.observacion,
    };

    const baseBody = {
      fecha_analisis: values.fecha_analisis.toISOString(),
      identificacion_muestras: values.identificacion_muestras,
      alimentacion: values.alimentacion,
      ultimo_antiparasitario: values.ultimo_antiparasitario,
      fecha_ultimo_tratamiento: values.fecha_ultimo_tratamiento?.toISOString() ?? null,
      comentarios_lote: values.comentarios_lote,
      muestras_coprocultivo: [muestra],
    };

    try {
      if (isEdit && uidAnalisis) {
        await updateMutation.mutateAsync({ uidProtocolo, uidAnalisis, data: baseBody });
        notify.success('Análisis Coprocultivo actualizado.');
      } else {
        await createMutation.mutateAsync({
          uidProtocolo,
          data: {
            ...baseBody,
            tipo_analisis: TipoAnalisis.COPROCULTIVO,
            uid_analisis_hpg_referencia: null,
          },
        });
        notify.success('Análisis Coprocultivo creado.');
        clearDraft(uidProtocolo);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      if (isApiError(err) && err.isConflict()) {
        setShowLockedDialog(true);
      } else {
        setSubmitError(err);
      }
    }
  }

  const subtitleParts = [
    numeroProtocolo != null ? `Protocolo N° ${numeroProtocolo}` : null,
    establecimientoNombre ?? null,
  ].filter(Boolean);
  const subtitle = subtitleParts.length > 0 ? subtitleParts.join(' · ') : null;

  const dialogTitle = (() => {
    if (!isEdit) return 'Nuevo análisis Coprocultivo';
    const numFmt =
      numeroProtocolo != null
        ? formatNumeroAnalisis(numeroProtocolo, coproQuery.data?.numero_analisis ?? null)
        : null;
    return numFmt ? `Editar análisis Coprocultivo ${numFmt}` : 'Editar análisis Coprocultivo';
  })();

  const isLoadingContent = isEdit && coproQuery.isLoading;
  const isErrorContent = isEdit && coproQuery.isError;
  const showForm = !isLoadingContent && !isErrorContent;

  const datosLabel = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span>Datos del análisis</span>
      {tabDatosHasError && <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />}
    </Box>
  );

  const resultadoLabel = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span>Resultado parasitológico</span>
      {tabResultadoHasError && <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />}
    </Box>
  );

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        fullScreen={isSmall}
        onClose={handleClose}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            pb: subtitle ? 1 : 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>{dialogTitle}</Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleClose} aria-label="Cerrar" size="small" sx={{ mt: 0.5 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {showForm && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabActiva}
              onChange={(_, v: TabId) => setTabActiva(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ px: 3 }}
            >
              <Tab value="datos" label={datosLabel} />
              <Tab value="resultado" label={resultadoLabel} />
            </Tabs>
          </Box>
        )}

        {showForm && pendingDraft && (
          <Box sx={{ px: 3, pt: 2, pb: 2 }}>
            <Alert
              severity="info"
              sx={{
                bgcolor: 'rgba(90, 28, 112, 0.10)',
                color: '#3d1350',
                '& .MuiAlert-icon': { color: '#5A1C70' },
              }}
              action={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button size="small" variant="outlined" onClick={handleRestoreDraft}>
                    Restaurar
                  </Button>
                  <Button size="small" color="inherit" onClick={handleDiscardDraft}>
                    Descartar
                  </Button>
                </Box>
              }
            >
              Tenés un borrador guardado{' '}
              {formatDistanceToNow(new Date(pendingDraft.timestamp), {
                addSuffix: true,
                locale: es,
              })}.
            </Alert>
          </Box>
        )}

        <DialogContent dividers sx={{ px: 3, py: 1 }}>
          {isLoadingContent && (
            <Box>
              <Skeleton variant="rounded" height={200} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={320} />
            </Box>
          )}

          {isErrorContent && (
            <Alert severity="error">{getErrorMessage(coproQuery.error)}</Alert>
          )}

          {showForm && (
            <>
              {!!submitError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {getErrorMessage(submitError)}
                </Alert>
              )}

              {tabActiva === 'datos' && <AnalisisDatosGeneralesForm noPaper />}

              {tabActiva === 'resultado' && (
                <>
                  {hpgRefNumeros.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Este coprocultivo está vinculado a la{hpgRefNumeros.length > 1 ? 's' : ''} muestra{hpgRefNumeros.length > 1 ? 's' : ''} HPG N° {hpgRefNumeros.join(', ')}.
                    </Alert>
                  )}
                  {sumError && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Corregí los porcentajes para que sumen 100 o 0.
                    </Alert>
                  )}
                  <ResultadoParasitologicoSection />
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={isPending || (isEdit && !isDirty)}
            onClick={() => void handleSubmit(handleSave, handleSaveError)()}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isPending ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discard confirmation */}
      <Dialog open={showDiscardDialog} maxWidth="xs" onClose={() => setShowDiscardDialog(false)}>
        <DialogTitle>Descartar cambios</DialogTitle>
        <DialogContent>
          <DialogContentText>¿Descartar los cambios sin guardar?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiscardDialog(false)}>Seguir editando</Button>
          <Button color="error" onClick={handleConfirmDiscard}>
            Descartar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Protocolo cerrado */}
      <Dialog open={showLockedDialog} maxWidth="xs">
        <DialogTitle>Protocolo cerrado</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Este protocolo está cerrado y ya no admite cambios.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowLockedDialog(false);
              onClose();
            }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
